import React, {
  memo, useEffect, useMemo, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { getPopupInfo } from '$/api/canvasRquest';
import {
  PopupPosition,
  type PopupInfoResponse,
} from '$/api/server.types';
import { useLanguageStore } from '$/store/languageStore';
import { mtstatTrack } from '$/utils/mtstat';
import styles from './index.module.scss';

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const DEFAULT_LEFT_MS = (5 * DAY_MS) + (23 * HOUR_MS) + (5 * MINUTE_MS) + SECOND_MS;

type TimeValue = number | string | Date;
type PopupItem = PopupInfoResponse['popup'][number] & { postion?: PopupInfoResponse['popup'][number]['position']; };

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface CountDownProps {
  className?: string;
  style?: React.CSSProperties;
  title?: React.ReactNode;
  targetTime?: TimeValue;
  initialLeftMs?: number;
  serverTime?: TimeValue;
  backgroundImageUrl?: string;
  useHomeTopBanner?: boolean;
  labels?: Partial<Record<keyof CountdownTime, string>>;
  onFinished?: () => void;
}

/**
 * 判断配置项是否为订阅页顶部营销位，兼容历史误拼的 postion 字段。
 */
function isMemberMarketingAlert(item: PopupItem) {
  return item.position === PopupPosition.MEMBER_YINGXIAO_ALERT
    || item.postion === PopupPosition.MEMBER_YINGXIAO_ALERT;
}

/**
 * 从 common/config 的 popup 列表中取订阅页顶部营销配置。
 */
function getMemberMarketingAlert(data: PopupInfoResponse) {
  return data.popup?.find((item) => (
    isMemberMarketingAlert(item as PopupItem)
    && (item.ext?.foreground?.img?.url || item.ext?.background?.img?.url)
  )) as PopupItem | undefined;
}

/**
 * 将时间入参统一成毫秒时间戳，兼容接口常见的秒级时间戳。
 */
function normalizeTimeMs(time?: TimeValue) {
  if (time === undefined || time === null || time === '') return undefined;
  if (time instanceof Date) {
    const dateTimeMs = time.getTime();
    return Number.isFinite(dateTimeMs) ? dateTimeMs : undefined;
  }
  if (typeof time === 'string') {
    const parsedTime = Date.parse(time);
    return Number.isFinite(parsedTime) ? parsedTime : undefined;
  }
  if (!Number.isFinite(time)) return undefined;
  return time < 10000000000 ? time * SECOND_MS : time;
}

/**
 * 计算当前剩余时间，支持接口下发结束时间或直接下发剩余毫秒数。
 */
function getLeftMs(targetTime: TimeValue | undefined, initialLeftMs: number | undefined, serverTime: TimeValue | undefined, startTimeMs: number) {
  const targetTimeMs = normalizeTimeMs(targetTime);
  if (targetTimeMs !== undefined) {
    const serverTimeMs = normalizeTimeMs(serverTime);
    const serverOffsetMs = serverTimeMs !== undefined ? serverTimeMs - startTimeMs : 0;
    return Math.max(targetTimeMs - Date.now() - serverOffsetMs, 0);
  }

  const durationMs = Number.isFinite(initialLeftMs) ? initialLeftMs! : DEFAULT_LEFT_MS;
  return Math.max(durationMs - (Date.now() - startTimeMs), 0);
}

/**
 * 将毫秒剩余量拆成设计稿需要展示的天、时、分、秒。
 */
function splitLeftMs(leftMs: number): CountdownTime {
  const days = Math.floor(leftMs / DAY_MS);
  const hours = Math.floor((leftMs % DAY_MS) / HOUR_MS);
  const minutes = Math.floor((leftMs % HOUR_MS) / MINUTE_MS);
  const seconds = Math.floor((leftMs % MINUTE_MS) / SECOND_MS);

  return {
    days,
    hours,
    minutes,
    seconds,
  };
}

/**
 * 数字块按两位补齐，超过两位时保留真实天数。
 */
function formatTimePart(value: number) {
  return value > 99 ? `${value}` : `${value}`.padStart(2, '0');
}

/**
 * 渲染会员活动倒计时横幅，并将活动时间源和视觉背景解耦。
 */
function CountDown(props: CountDownProps) {
  const {
    className,
    style,
    title,
    targetTime,
    initialLeftMs,
    serverTime,
    backgroundImageUrl,
    useHomeTopBanner = true,
    labels,
    onFinished,
  } = props;
  const languageStore = useLanguageStore((state) => state.languageStore);
  const startTimeRef = useRef(Date.now());
  const finishedRef = useRef(false);
  const reportedShowKeysRef = useRef(new Set<string>());
  const [bannerInfo, setBannerInfo] = useState<PopupItem | null>(null);
  const [bannerServerTime, setBannerServerTime] = useState<TimeValue | undefined>();
  const [visualReady, setVisualReady] = useState(!backgroundImageUrl);
  const effectiveTargetTime = targetTime ?? bannerInfo?.end_time;
  const effectiveServerTime = serverTime ?? bannerServerTime;
  const effectiveBackgroundImageUrl = backgroundImageUrl
    ?? bannerInfo?.ext?.background?.img?.url
    ?? bannerInfo?.ext?.img?.url;
  const effectiveForegroundImageUrl = bannerInfo?.ext?.foreground?.img?.url;
  const hasVisualImage = Boolean(effectiveBackgroundImageUrl || effectiveForegroundImageUrl);
  const effectiveTitle = title ?? (hasVisualImage ? null : (bannerInfo?.title || '夏至狂欢 低至五折'));
  const hasManualContent = Boolean(
    title
    || targetTime
    || initialLeftMs
    || serverTime
    || backgroundImageUrl,
  );
  const [leftMs, setLeftMs] = useState(() => getLeftMs(effectiveTargetTime, initialLeftMs, effectiveServerTime, startTimeRef.current));
  const countdown = useMemo(() => splitLeftMs(leftMs), [leftMs]);
  // 倒计时单位跟随当前语言切换，同时保留业务方自定义 labels 的覆盖能力。
  const unitLabels = useMemo(() => ({
    days: languageStore.smartcatTranslationText((v) => v.days_left),
    hours: languageStore.smartcatTranslationText((v) => v.hours_left),
    minutes: languageStore.smartcatTranslationText((v) => v.minutes_left),
    seconds: languageStore.smartcatTranslationText((v) => v.seconds_left),
    ...labels,
  }), [labels, languageStore]);

  useEffect(() => {
    if (!useHomeTopBanner) {
      setBannerInfo(null);
      setBannerServerTime(undefined);
      return undefined;
    }

    let cancelled = false;

    /**
     * 拉取订阅页顶部营销配置，避免与首页 banner 运营位互相影响。
     */
    const requestMemberMarketingAlert = async () => {
      try {
        const popupInfo = await getPopupInfo();
        if (cancelled) return;
        setBannerInfo(getMemberMarketingAlert(popupInfo) || null);
        setBannerServerTime(popupInfo.server_time);
      } catch (error) {
        if (cancelled) return;
        console.error('CountDown request member_yingxiao_alert error', error);
        setBannerInfo(null);
        setBannerServerTime(undefined);
      }
    };

    requestMemberMarketingAlert();

    return () => {
      cancelled = true;
    };
  }, [useHomeTopBanner]);

  useEffect(() => {
    const visualImageUrls = [effectiveBackgroundImageUrl, effectiveForegroundImageUrl].filter(Boolean) as string[];
    if (visualImageUrls.length === 0) {
      setVisualReady(true);
      return undefined;
    }

    let cancelled = false;
    setVisualReady(false);

    /** 等待背景和前景素材全部可用，防止营销条分层闪烁。 */
    const preloadVisualImage = (url: string) => new Promise<void>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`Failed to load marketing image: ${url}`));
      image.src = url;
      if (image.complete && image.naturalWidth > 0) resolve();
    });

    Promise.all(visualImageUrls.map(preloadVisualImage))
      .then(() => {
        if (!cancelled) setVisualReady(true);
      })
      .catch(() => {
        if (!cancelled) setVisualReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, [effectiveBackgroundImageUrl, effectiveForegroundImageUrl]);

  /** 订阅弹窗顶部营销图加载完成并真实渲染后，按内容 ID 上报一次曝光。 */
  useEffect(() => {
    const usesConfiguredForeground = Boolean(
      effectiveForegroundImageUrl
      && effectiveForegroundImageUrl === bannerInfo?.ext.foreground?.img.url,
    );
    const usesConfiguredBackground = Boolean(
      effectiveBackgroundImageUrl
      && (
        effectiveBackgroundImageUrl === bannerInfo?.ext.background?.img.url
        || effectiveBackgroundImageUrl === bannerInfo?.ext.img?.url
      ),
    );

    if (
      !useHomeTopBanner
      || !bannerInfo
      || (!usesConfiguredForeground && !usesConfiguredBackground)
      || !visualReady
    ) return;

    const contentId = String(bannerInfo.id);
    if (reportedShowKeysRef.current.has(contentId)) return;
    reportedShowKeysRef.current.add(contentId);
    mtstatTrack('roboneo_homepage_sales_show', {
      content_id: contentId,
      from_source: 'subscription_page',
    });
  }, [
    bannerInfo,
    effectiveBackgroundImageUrl,
    effectiveForegroundImageUrl,
    useHomeTopBanner,
    visualReady,
  ]);

  useEffect(() => {
    startTimeRef.current = Date.now();
    finishedRef.current = false;

    /**
     * 以秒级粒度刷新展示，并确保结束回调只触发一次。
     */
    const updateCountdown = () => {
      const nextLeftMs = getLeftMs(effectiveTargetTime, initialLeftMs, effectiveServerTime, startTimeRef.current);
      setLeftMs(nextLeftMs);
      if (nextLeftMs === 0 && !finishedRef.current) {
        finishedRef.current = true;
        onFinished?.();
      }
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, SECOND_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [effectiveServerTime, effectiveTargetTime, initialLeftMs, onFinished]);

  const timeItems = useMemo(() => {
    return (Object.keys(unitLabels) as Array<keyof CountdownTime>).map((key) => ({
      key,
      label: unitLabels[key],
      value: formatTimePart(countdown[key]),
    }));
  }, [countdown, unitLabels]);

  if (
    useHomeTopBanner
    && !bannerInfo
    && !hasManualContent
  ) {
    return null;
  }

  if (hasVisualImage && !visualReady) {
    return null;
  }

  return (
    <div
      className={classNames(styles.countdown, {
        [styles['image-mode']]: hasVisualImage,
      }, className)}
      style={style}
      aria-label={`活动倒计时 ${timeItems.map((item) => `${item.value}${item.label}`).join(' ')}`}
    >
      {effectiveBackgroundImageUrl ? (
        <img
          className={styles['background-image']}
          src={effectiveBackgroundImageUrl}
          alt=""
          aria-hidden
        />
      ) : null}
      {effectiveForegroundImageUrl ? (
        <img
          className={styles['foreground-image']}
          src={effectiveForegroundImageUrl}
          alt=""
          aria-hidden
        />
      ) : null}
      {effectiveTitle ? <div className={styles.title}>{effectiveTitle}</div> : null}
      <div className={styles['time-list']}>
        {timeItems.map((item) => (
          <div className={styles['time-card']} key={item.key}>
            <div className={styles['time-label']}>{item.label}</div>
            <div className={styles['time-value']}>{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export type { CountDownProps };
export default memo(CountDown);
